import * as Contentful from 'contentful';

import {
  parsePricingStrategy,
  PricingPlan,
  PricingTier,
  PricingTierPrice
} from './models';

export type ProductContentSkeleton = {
  contentTypeId: string;
  fields: ProductContent;
};

export type ProductContent = {
  name?: Contentful.EntryFields.Text;
  platform?: Contentful.EntrySkeletonType<PlatformContent>;
  catalogItemId?: Contentful.EntryFields.Text;
  description: Contentful.EntryFields.Text;
  productFamily?: Contentful.EntryFields.Text;
  productType?: Contentful.EntryFields.Text;
  unitLabel?: Contentful.EntryFields.Text;
  billingType?: Contentful.EntryFields.Text;
  vendor?: Contentful.EntrySkeletonType<VendorContent>;
  parentProducts?: ProductContent[];
  parentProductName?: Contentful.EntryFields.Text;
  defaultPricingPlanMonthly: Contentful.EntrySkeletonType<PricingPlanContent> | null;
  defaultPricingPlanYearly: Contentful.EntrySkeletonType<PricingPlanContent> | null;
  defaultStdPricingPlanMonthly: Contentful.EntrySkeletonType<PricingPlanContent> | null;
  defaultStdPricingPlanYearly: Contentful.EntrySkeletonType<PricingPlanContent> | null;
  defaultFreePricingPlanMonthly: Contentful.EntrySkeletonType<PricingPlanContent> | null;
  defaultFreePricingPlanYearly: Contentful.EntrySkeletonType<PricingPlanContent> | null;
  defaultPremiumPricingPlanMonthly: Contentful.EntrySkeletonType<PricingPlanContent> | null;
  defaultPremiumPricingPlanYearly: Contentful.EntrySkeletonType<PricingPlanContent> | null;
  defaultEnterprisePricingPlanMonthly: Contentful.EntrySkeletonType<PricingPlanContent> | null;
  defaultEnterprisePricingPlanYearly: Contentful.EntrySkeletonType<PricingPlanContent> | null;
  key?: Contentful.EntryFields.Text;
  sourceSequence?: Contentful.EntryFields.Number;
};

export type PlatformContent = {
  readonly name?: Contentful.EntryFields.Text;
};

export type VendorContent = {
  readonly name?: Contentful.EntryFields.Text;
};
export type PricingPlanContent = {
  configuration: PricingPlanContentConfigurationItem[];
  product?: Contentful.EntrySkeletonType<ProductContent>;
  periodUnit?: string;
  periodCount?: number;
  status?: string;
  edition?: string;
  pricingPlanId?: string;
  sourceSequence?: number;
};

export type PricingPlanContentConfiguration =
  ReadonlyArray<PricingPlanContentConfigurationItem>;

export type PricingPlanContentConfigurationItem = {
  readonly starter: boolean;
  readonly pricing_strategy: string;
  readonly unit_description: string;
  readonly unit_limit_lower: number | null;
  readonly unit_limit_upper: number | null;
  readonly prices: ReadonlyArray<PricingPlanContentConfigurationItemPrice>;
};

export type PricingPlanContentConfigurationItemPrice = {
  readonly currency: string;
  readonly unit_price: number;
};

export function mapPricingPlanContentEntryToPricingPlan(
  entry: Contentful.EntrySkeletonType<PricingPlanContent> | null
): PricingPlan | undefined {
  if (entry) {
    return mapPricingPlanContentToPricingPlan(entry.fields.configuration);
  } else {
    return undefined;
  }
}

function mapPricingPlanContentToPricingPlan(
  content: PricingPlanContentConfiguration
): PricingPlan {
  return content.map(mapPricingPlanContentConfigurationItemToPricingTier);
}

function mapPricingPlanContentConfigurationItemToPricingTier({
  starter,
  pricing_strategy,
  unit_description,
  unit_limit_lower,
  unit_limit_upper,
  prices
}: PricingPlanContentConfigurationItem): PricingTier {
  return {
    starter,
    pricingStrategy: parsePricingStrategy(pricing_strategy),
    unitDescription: unit_description,
    unitLimitLower: unit_limit_lower,
    unitLimitUpper: unit_limit_upper,
    prices: prices.map(
      mapPricingPlanContentConfigurationItemPriceToPricingTierPrice
    )
  };
}

function mapPricingPlanContentConfigurationItemPriceToPricingTierPrice({
  currency,
  unit_price
}: PricingPlanContentConfigurationItemPrice): PricingTierPrice {
  return {
    currency,
    unitPrice: unit_price
  };
}
